import React, { useState, useEffect } from "react";
import {
  FormControl,
  FormHelperText,
  Paper,
  FormLabel,
  Select as MuiSelect,
  MenuItem,
  Box,
  Typography,
  Button,
} from "@mui/material";
import PropTypes from "prop-types";
import {
  convertToDefEventPara,
  getAPIEndpoint,
  handleAPICall,
} from "../helper.js";
import { Clear, Preview } from "@mui/icons-material";
import { useSnackbar } from "notistack";
import DocumentViewer from "../Viewer/DocumentViewer.jsx";
import { componentTokens } from "@accredo/fe-shared/theme/tokens/components.js";
import { dimensions } from "@accredo/fe-shared/theme/index.js";

SelectList.propTypes = {
  item: PropTypes.object.isRequired,
  value: PropTypes.any,
  error: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  showErrorText: PropTypes.bool,
  variant: PropTypes.string,
  size: PropTypes.string,
  disabled: PropTypes.bool,
  apiGet: PropTypes.func.isRequired,
};

const getSelectedOptions = (options) => {
  let result = [];
  if (options) {
    result = options;
    if (typeof options === "string") result = JSON.parse(options);
  }
  return result;
};

export default function SelectList(props) {
  const {
    item,
    value = "",
    values,
    error = null,
    onChange,
    showErrorText = false,
    variant = "filled",
    size = "small",
    disabled = false,
    apiGet,
  } = props;
  const [isLoading, setIsLoading] = useState(true);
  const [options, setOptions] = useState(item.options || null);
  const [selectableOptions, setSelectableOptions] = useState(null);
  const [selectedOptions, setSelectedOptions] = useState(
    getSelectedOptions(value)
  );
  const [lastAPIEndpoint, setLastAPIEndpoint] = useState("");
  const [selectedValue, setSelectedValue] = useState(undefined);
  const [documentToView, setDocumentToView] = useState(null);
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    const get_api =
      typeof item.get_api === "function" ? item.get_api(values) : item.get_api;

    if (
      values &&
      item.get_api &&
      lastAPIEndpoint !== getAPIEndpoint(get_api, values)
    )
      setLastAPIEndpoint(
        handleAPICall(
          get_api,
          values,
          apiGet,
          (data) => {
            if (get_api.setResult) {
              setOptions(get_api.setResult(data));
            } else {
              setOptions(data);
            }
          },
          setIsLoading,
          get_api.params
        )
      );
    else setIsLoading(false);
  }, [item.get_api, apiGet]);

  useEffect(() => {
    if (options)
      setSelectableOptions(
        options.filter(
          (option) =>
            !selectedOptions.find(
              (selectedOption) => selectedOption.key === option.key
            )
        )
      );
  }, [options]);

  useEffect(() => {
    setSelectedValue(undefined);
    onChange(convertToDefEventPara(item.name, selectedOptions));
  }, [selectedOptions]);

  const onSelect = (e) => {
    const key = e.target.value.key;
    setSelectedOptions([
      ...selectedOptions,
      options.find((option) => option.key === key),
    ]);
    setSelectableOptions(
      selectableOptions.filter((option) => option.key !== key)
    );
  };

  const removeSelectedOption = (key) => {
    setSelectedOptions(selectedOptions.filter((option) => option.key !== key));
    setSelectableOptions([
      ...selectableOptions,
      options.find((option) => option.key === key),
    ]);
  };

  const handleLink = async (rowItem) => {
    let endpoint = item.preview.endpoint;
    switch (item.preview.method) {
      case "get":
        if (rowItem && item.preview.eval_endpoint) {
          endpoint = getAPIEndpoint(item.preview, rowItem);
        }
        await apiGet(endpoint, {
          label: "Dokument",
          setIsLoading: setIsLoading,
          headers: item.get_api.headers,
          setResult: setDocumentToView,
          enqueueSnackbar: enqueueSnackbar,
        });
        break;
      case "data":
        break;
      default:
    }
  };

  let helperText = "";
  if (showErrorText) helperText = error;

  if (isLoading) return <></>;
  else
    return (
      <Paper
        elevation={0}
        sx={{
          backgroundColor: componentTokens.formSection.backgroundColor,
          borderRadius: componentTokens.formSection.borderRadius,
          height: "100%",
          width: "100%",
          p: componentTokens.formSection.padding,
          margin: "0px",
        }}
      >
        <FormLabel
          filled={true}
          sx={{ mb: componentTokens.formSection.padding, display: "block" }}
        >
          {item.label}
        </FormLabel>
        <FormControl
          variant={variant}
          {...(error && { error: true })}
          sx={{ width: "100%" }}
          size={size}
        >
          {selectableOptions && (
            <MuiSelect
              value={selectedValue || ""}
              disabled={disabled}
              onChange={onSelect}
            >
              {selectableOptions.map((sitem) => (
                <MenuItem key={sitem.key} value={sitem}>
                  {sitem.label}
                </MenuItem>
              ))}
            </MuiSelect>
          )}
          <FormHelperText>{helperText}</FormHelperText>
        </FormControl>
        <Box sx={{ mt: dimensions["xl"], pl: "0px", pr: "0px" }}>
          {selectedOptions &&
            selectedOptions.map((item, index) => (
              <Box
                key={index}
                sx={{
                  mt: "5px",
                  paddingRight: componentTokens.listItem.paddingRight,
                  paddingY: componentTokens.listItem.paddingY,
                  display: "flex",
                  alignItems: "flex-start",
                  justifyContent: "space-between",
                }}
              >
                <Box
                  sx={{ display: "flex", alignItems: "center", height: "36px" }}
                >
                  <Typography variant="regular_text_12px_medium">
                    {item.label}
                  </Typography>
                </Box>

                <Box sx={{ display: "flex" }}>
                  <Button
                    sx={{
                      height: componentTokens.listItem.actionButton.size,
                      minWidth: componentTokens.listItem.actionButton.size,
                      width: componentTokens.listItem.actionButton.size,
                      backgroundColor: "transparent",
                      "&:hover": {
                        backgroundColor:
                          componentTokens.listItem.actionButton.remove
                            .hoverBackground,
                        boxShadow: "none",
                      },
                    }}
                    disabled={disabled}
                    onClick={(e) => removeSelectedOption(item.key)}
                  >
                    <Clear
                      sx={{
                        color:
                          componentTokens.listItem.actionButton.remove
                            .iconColor,
                      }}
                    />
                  </Button>
                  &nbsp;
                  <Button
                    sx={{
                      height: componentTokens.listItem.actionButton.size,
                      minWidth: componentTokens.listItem.actionButton.size,
                      width: componentTokens.listItem.actionButton.size,
                      backgroundColor: "transparent",
                      "&:hover": {
                        backgroundColor:
                          componentTokens.listItem.actionButton.preview
                            .hoverBackground,
                        boxShadow: "none",
                      },
                    }}
                    color="primary"
                    disabled={disabled}
                    onClick={(e) => handleLink(item)}
                  >
                    <Preview />
                  </Button>
                </Box>
              </Box>
            ))}
          <DocumentViewer document={documentToView} />
        </Box>
      </Paper>
    );
}
